/**
 * planningScoring.js — Section Completion + Critical Field Scoring Engine
 *
 * Computes scoring at two levels:
 *   1. Section-level: per-step completion, readiness, and status
 *   2. Event-level: aggregated across all sections
 *
 * SCORING MODEL:
 *   Completion Score = filled visible fields / total visible fields (0–1)
 *   Readiness Score  = weighted filled / weighted total (0–1)
 *     Weights: critical = 3, required = 2, optional = 1
 *
 * STATUS LOGIC:
 *   not_started     — 0 visible fields filled
 *   in_progress     — some filled, all critical filled
 *   needs_attention — critical or required fields are missing
 *   complete        — all visible critical + required fields filled
 *
 * VISIBILITY-AWARE: Only evaluates currently visible fields.
 * Hidden fields are excluded from all scoring/validation.
 *
 * No backend calls. No mutations. Pure client-side derivation.
 */

import { getPlanningSchema, isFieldVisible } from '../config/planningSchemas';

/* ── Weights ── */
const WEIGHT_CRITICAL = 3;
const WEIGHT_REQUIRED = 2;
const WEIGHT_OPTIONAL = 1;

/* ── Helpers ── */

function fieldWeight(field) {
  if (field.critical) return WEIGHT_CRITICAL;
  if (field.required) return WEIGHT_REQUIRED;
  return WEIGHT_OPTIONAL;
}

function isFilled(field, answers) {
  const v = answers[field.id];
  if (v === undefined || v === null || v === '') return false;
  if (typeof v === 'string' && !v.trim()) return false;
  // Repeater arrays (e.g. dynamic sibling list) count as filled if any
  // row contains at least one non-empty value.
  if (Array.isArray(v)) {
    return v.some((row) => row && Object.values(row).some((c) => (typeof c === 'string' ? c.trim() : c)));
  }
  return true;
}

function deriveStatus(fields) {
  const { total, filled, criticalTotal, criticalFilled, requiredTotal, requiredFilled } = fields;
  if (total === 0) return 'complete';
  if (filled === 0) return 'not_started';
  if (criticalFilled < criticalTotal || requiredFilled < requiredTotal) return 'needs_attention';
  if (filled < total) return 'in_progress';
  return 'complete';
}

/* ── Section-level scoring ── */

/**
 * Compute scoring for a single section (schema step).
 *
 * @param {Object} step — schema step { id, title, description, fields }
 * @param {Object} answers — full answers object
 * @returns {Object} section score
 */
export function computeSectionScore(step, answers) {
  if (!step?.fields || !answers) {
    return emptySectionScore(step?.id, step?.title);
  }

  const visibleFields = step.fields.filter(f => isFieldVisible(f, answers));

  let total = 0, filled = 0;
  let criticalTotal = 0, criticalFilled = 0;
  let requiredTotal = 0, requiredFilled = 0;
  let optionalTotal = 0, optionalFilled = 0;
  let weightedTotal = 0, weightedFilled = 0;
  const criticalMissing = [];
  const requiredMissing = [];

  for (const field of visibleFields) {
    const w = fieldWeight(field);
    const filled_ = isFilled(field, answers);
    total++;
    weightedTotal += w;

    if (filled_) {
      filled++;
      weightedFilled += w;
    }

    if (field.critical) {
      criticalTotal++;
      if (filled_) criticalFilled++;
      else criticalMissing.push({ field: field.id, label: field.label });
    } else if (field.required) {
      requiredTotal++;
      if (filled_) requiredFilled++;
      else requiredMissing.push({ field: field.id, label: field.label });
    } else {
      optionalTotal++;
      if (filled_) optionalFilled++;
    }
  }

  const completionScore = total > 0 ? filled / total : 1;
  const readinessScore = weightedTotal > 0 ? weightedFilled / weightedTotal : 1;

  const fields = {
    total, filled,
    criticalTotal, criticalFilled,
    requiredTotal, requiredFilled,
    optionalTotal, optionalFilled,
  };

  return {
    sectionId: step.id,
    sectionTitle: step.title || step.id,
    status: deriveStatus(fields),
    completionScore: Math.round(completionScore * 100) / 100,
    readinessScore: Math.round(readinessScore * 100) / 100,
    completionPercent: Math.round(completionScore * 100),
    readinessPercent: Math.round(readinessScore * 100),
    fields,
    criticalMissing,
    requiredMissing,
  };
}

/* ── Event-level scoring ── */

/**
 * Compute scoring across all sections for an event type.
 *
 * @param {string} eventType — event type key
 * @param {Object} answers — full answers object
 * @returns {Object} event-level score with section breakdown
 */
export function computeEventScore(eventType, answers) {
  const schema = getPlanningSchema(eventType);
  if (!schema?.steps || !answers) return emptyEventScore();

  const sections = schema.steps.map(step => computeSectionScore(step, answers));

  // Aggregate across all sections
  let totalFields = 0, filledFields = 0;
  let totalCritical = 0, filledCritical = 0;
  let totalRequired = 0, filledRequired = 0;
  let totalWeighted = 0, filledWeighted = 0;
  const allCriticalMissing = [];
  const allRequiredMissing = [];

  for (const s of sections) {
    totalFields += s.fields.total;
    filledFields += s.fields.filled;
    totalCritical += s.fields.criticalTotal;
    filledCritical += s.fields.criticalFilled;
    totalRequired += s.fields.requiredTotal;
    filledRequired += s.fields.requiredFilled;
    totalWeighted += (s.fields.criticalTotal * WEIGHT_CRITICAL) +
                     (s.fields.requiredTotal * WEIGHT_REQUIRED) +
                     (s.fields.optionalTotal * WEIGHT_OPTIONAL);
    filledWeighted += (s.fields.criticalFilled * WEIGHT_CRITICAL) +
                      (s.fields.requiredFilled * WEIGHT_REQUIRED) +
                      (s.fields.optionalFilled * WEIGHT_OPTIONAL);

    for (const m of s.criticalMissing) {
      allCriticalMissing.push({ ...m, section: s.sectionId, sectionTitle: s.sectionTitle });
    }
    for (const m of s.requiredMissing) {
      allRequiredMissing.push({ ...m, section: s.sectionId, sectionTitle: s.sectionTitle });
    }
  }

  const completionScore = totalFields > 0 ? filledFields / totalFields : 1;
  const readinessScore = totalWeighted > 0 ? filledWeighted / totalWeighted : 1;

  const aggregateFields = {
    total: totalFields,
    filled: filledFields,
    criticalTotal: totalCritical,
    criticalFilled: filledCritical,
    requiredTotal: totalRequired,
    requiredFilled: filledRequired,
  };

  return {
    status: deriveStatus(aggregateFields),
    completionScore: Math.round(completionScore * 100) / 100,
    readinessScore: Math.round(readinessScore * 100) / 100,
    completionPercent: Math.round(completionScore * 100),
    readinessPercent: Math.round(readinessScore * 100),
    fields: aggregateFields,
    sections,
    criticalMissing: allCriticalMissing,
    requiredMissing: allRequiredMissing,
    sectionCount: sections.length,
    sectionsComplete: sections.filter(s => s.status === 'complete').length,
    sectionsNeedAttention: sections.filter(s => s.status === 'needs_attention').length,
  };
}

/* ── Status metadata ── */

const STATUS_META = {
  not_started:     { label: 'Not Started',     color: '#6B7280', bg: 'rgba(107,114,128,0.1)', border: 'rgba(107,114,128,0.2)' },
  in_progress:     { label: 'In Progress',     color: '#3B82F6', bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.2)' },
  needs_attention: { label: 'Needs Attention',  color: '#F59E0B', bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.2)' },
  complete:        { label: 'Complete',         color: '#22C55E', bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.2)' },
};

/**
 * Get visual metadata for a status key.
 */
export const getStatusMeta = (status) => {
  return STATUS_META[status] || STATUS_META.not_started;
};

/**
 * Get a human-readable readiness label from a readiness score.
 */
export const getReadinessLabel = (score) => {
  if (score >= 1) return 'Fully Ready';
  if (score >= 0.8) return 'Almost Ready';
  if (score >= 0.5) return 'Partially Ready';
  if (score > 0) return 'Getting Started';
  return 'Not Started';
};

/* ── Fallbacks ── */

function emptySectionScore(id, title) {
  return {
    sectionId: id || '',
    sectionTitle: title || '',
    status: 'not_started',
    completionScore: 0,
    readinessScore: 0,
    completionPercent: 0,
    readinessPercent: 0,
    fields: { total: 0, filled: 0, criticalTotal: 0, criticalFilled: 0, requiredTotal: 0, requiredFilled: 0, optionalTotal: 0, optionalFilled: 0 },
    criticalMissing: [],
    requiredMissing: [],
  };
}

function emptyEventScore() {
  return {
    status: 'not_started',
    completionScore: 0,
    readinessScore: 0,
    completionPercent: 0,
    readinessPercent: 0,
    fields: { total: 0, filled: 0, criticalTotal: 0, criticalFilled: 0, requiredTotal: 0, requiredFilled: 0 },
    sections: [],
    criticalMissing: [],
    requiredMissing: [],
    sectionCount: 0,
    sectionsComplete: 0,
    sectionsNeedAttention: 0,
  };
}
